import { useCallback, useEffect, useRef, useState } from 'react'
import {
  mergeTransactionBatch,
  countItems,
  canExpand,
  nextWindow,
  nextWindowSeconds,
  initialWindowEnd,
  INITIAL_WINDOW_SECONDS,
  MAX_WINDOW_SECONDS,
  SAFETY_CAP_PER_TYPE,
} from '../utils/transactionFeed'

/**
 * Time-windowed transaction feed shared by Global/Token/Pair/Account pages.
 * Newest to oldest by default; each "load more" widens the window further from the
 * anchor, doubling it if a window comes back too thin, so dense and sparse periods
 * both paginate smoothly. Pass direction='asc' (via setDirection/setDateRange) to walk
 * oldest-to-newest instead - "Oldest first" then means fetching from the real start of
 * history forward, not just re-sorting whatever happens to already be loaded.
 *
 * A custom date range (`setDateRange`) uses the exact same windowed pagination,
 * just bounded by `from`/`to` instead of the epoch/now - so a wide range pages through
 * "load more" like the normal feed instead of being capped to one fetch.
 *
 * @param {(windowStart: number, windowEnd: number, safetyCap: number, direction: 'asc'|'desc') => Promise<{mints, burns, swaps}>} fetchWindow
 * @param {string|undefined} feedKey identifies what's being fetched (token/pair/account address, or a constant for global);
 *   changing it resets the accumulated feed and starts over.
 */
export default function useTransactionFeed(fetchWindow, feedKey) {
  const [feed, setFeed] = useState(null)
  const [isDateRangeFiltered, setIsDateRangeFiltered] = useState(false)
  const [loading, setLoading] = useState(false)
  const keyRef = useRef(feedKey)
  const loadingRef = useRef(false)

  useEffect(() => {
    keyRef.current = feedKey
    setFeed(null)
    setIsDateRangeFiltered(false)
  }, [feedKey])

  // Fetches windows starting at `anchor`, expanding toward `boundary` until non-thin,
  // then merges onto `prevFeed`. Shared by every pagination mode - they differ only in
  // direction/anchor/boundary/startWindowSeconds.
  //
  // startWindowSeconds defaults to a small 4h probe (for the unbounded "all history" feed,
  // where the eventual span is unknown). A bounded date range already knows its exact span,
  // so it's passed the full span instead - one request covers it instead of several rounds
  // of the probe doubling its way up to that same size.
  const runLoad = useCallback(
    async (direction, anchor, boundary, prevFeed, startWindowSeconds = INITIAL_WINDOW_SECONDS) => {
      if (!feedKey || loadingRef.current) {
        return
      }
      loadingRef.current = true
      setLoading(true)
      try {
        let windowSeconds = startWindowSeconds
        let { windowStart, windowEnd } = nextWindow(direction, anchor, boundary, windowSeconds)
        let batch = await fetchWindow(windowStart, windowEnd, SAFETY_CAP_PER_TYPE, direction)

        while (canExpand(direction, windowStart, windowEnd, boundary, countItems(batch), windowSeconds)) {
          windowSeconds = nextWindowSeconds(windowSeconds)
          ;({ windowStart, windowEnd } = nextWindow(direction, anchor, boundary, windowSeconds))
          batch = await fetchWindow(windowStart, windowEnd, SAFETY_CAP_PER_TYPE, direction)
        }

        // Ignore late responses that belong to a feedKey we've since navigated away from.
        if (keyRef.current !== feedKey) {
          return
        }
        const cursor = direction === 'asc' ? windowEnd : windowStart
        setFeed((current) => mergeTransactionBatch(current ?? prevFeed, batch, cursor, boundary, direction))
      } catch (e) {
        console.log(e)
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [feedKey, fetchWindow]
  )

  const loadNextBatch = useCallback(() => {
    const direction = feed?.direction ?? 'desc'
    const boundary = feed?.boundary ?? 0
    const anchor = feed?.cursor ?? (direction === 'asc' ? 0 : initialWindowEnd())
    // Continuing a bounded date range: size the next probe to what's actually left in the
    // range instead of the tiny default, so "load more" doesn't re-pay the multi-round
    // expansion cost. The unbounded "all history" feed has no known remaining span, so it
    // keeps starting each probe small - sizing it to `anchor - 0` there would mean one
    // "load more" jumping straight back to the dawn of time.
    const startWindowSeconds =
      feed && isDateRangeFiltered
        ? Math.min(Math.max(Math.abs(boundary - anchor), INITIAL_WINDOW_SECONDS), MAX_WINDOW_SECONDS)
        : INITIAL_WINDOW_SECONDS
    return runLoad(direction, anchor, boundary, feed, startWindowSeconds)
  }, [feed, isDateRangeFiltered, runLoad])

  useEffect(() => {
    if (feedKey && !feed && !loadingRef.current) {
      loadNextBatch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedKey, feed])

  // Restart the "all history" feed walking the given direction from scratch -
  // 'desc' from now backward (the default), 'asc' from the start of history forward.
  const setDirection = useCallback(
    (direction) => {
      setIsDateRangeFiltered(false)
      setFeed(null)
      const boundary = direction === 'asc' ? initialWindowEnd() : 0
      const anchor = direction === 'asc' ? 0 : initialWindowEnd()
      return runLoad(direction, anchor, boundary, null)
    },
    [runLoad]
  )

  // Replace the feed with the first page of an explicit [from, to) range, in either
  // direction. Paginates via the normal "load more" (hasMore/loadMore) same as the
  // unbounded feed, just bounded by from/to instead of 0/now.
  const setDateRange = useCallback(
    (from, to, direction = 'desc') => {
      setIsDateRangeFiltered(true)
      setFeed(null)
      const anchor = direction === 'asc' ? from : to
      const boundary = direction === 'asc' ? to : from
      const fullSpan = Math.min(Math.max(to - from, INITIAL_WINDOW_SECONDS), MAX_WINDOW_SECONDS)
      return runLoad(direction, anchor, boundary, null, fullSpan)
    },
    [runLoad]
  )

  // Drop the date-range filter and resume normal newest-to-oldest auto-pagination.
  const clearDateRange = useCallback(() => {
    setIsDateRangeFiltered(false)
    setFeed(null)
  }, [])

  return {
    transactions: feed ? { mints: feed.mints, burns: feed.burns, swaps: feed.swaps } : undefined,
    hasMore: feed?.hasMore ?? false,
    loading,
    loadMore: loadNextBatch,
    isDateRangeFiltered,
    direction: feed?.direction ?? 'desc',
    setDirection,
    setDateRange,
    clearDateRange,
  }
}
